/**
 * DataMoonAdminPage.tsx — SUPER_ADMIN DataMoon management UI.
 *
 * Under <PlatformAdminLayout>. Mounted at `/platform-admin/datamoon`.
 *
 * Two sections:
 *   1. Global Config — single API key + platform-wide defaults.
 *   2. Tenant Access — which tenants have access, their quotas, enable/disable.
 */
import { useState } from "react";
import type { FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  Database,
  Plus,
  Trash2,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { datamoonAdminApi } from "@/services/apiClient";
import { PageHeader } from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { NativeSelect } from "@/components/ui/select";
import { platformApi } from "@/services/apiClient";
import { formatDateTime } from "@/lib/utils";

// ── Types ─────────────────────────────────────────────────────────────────────

interface GlobalDataMoonConfig {
  id: number;
  api_key_masked: string;
  website_id: number | null;
  is_active: boolean;
  default_limit: number;
  max_retries: number;
  rate_limit_per_min: number;
  created_at: string;
  updated_at: string;
}

interface TenantAccessRow {
  id: number;
  tenant_id: number;
  tenant_name: string | null;
  tenant_slug: string | null;
  is_enabled: boolean;
  prospect_quota: number;
  quota_used: number;
  quota_reset_at: string;
  last_pull_at: string | null;
  created_at: string;
  updated_at: string;
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function DataMoonAdminPage() {
  const qc = useQueryClient();

  // ── Global config ──────────────────────────────────────────────────────────
  const configQuery = useQuery<GlobalDataMoonConfig | null>({
    queryKey: ["datamoon-admin-config"],
    queryFn: async () => {
      try {
        return await datamoonAdminApi.getConfig();
      } catch (e: unknown) {
        if ((e as { status?: number }).status === 404) return null;
        throw e;
      }
    },
  });

  // ── Tenant access list ─────────────────────────────────────────────────────
  const tenantsQuery = useQuery<TenantAccessRow[]>({
    queryKey: ["datamoon-admin-tenants"],
    queryFn: () => datamoonAdminApi.listTenantAccess(),
  });

  // ── Config form state ──────────────────────────────────────────────────────
  const [configOpen, setConfigOpen] = useState(false);
  const [cfgApiKey, setCfgApiKey] = useState("");
  const [cfgWebsiteId, setCfgWebsiteId] = useState("");
  const [cfgActive, setCfgActive] = useState(false);
  const [cfgDefaultLimit, setCfgDefaultLimit] = useState("500");
  const [cfgMaxRetries, setCfgMaxRetries] = useState("4");
  const [cfgRateLimit, setCfgRateLimit] = useState("100");

  function openConfigEdit() {
    const c = configQuery.data;
    if (c) {
      setCfgApiKey("");
      setCfgWebsiteId(c.website_id != null ? String(c.website_id) : "");
      setCfgActive(c.is_active);
      setCfgDefaultLimit(String(c.default_limit));
      setCfgMaxRetries(String(c.max_retries));
      setCfgRateLimit(String(c.rate_limit_per_min));
    } else {
      setCfgApiKey("");
      setCfgWebsiteId("");
      setCfgActive(false);
      setCfgDefaultLimit("500");
      setCfgMaxRetries("4");
      setCfgRateLimit("100");
    }
    setConfigOpen(true);
  }

  const configMut = useMutation({
    mutationFn: () =>
      datamoonAdminApi.upsertConfig({
        api_key: cfgApiKey || undefined,
        website_id: cfgWebsiteId ? Number(cfgWebsiteId) : null,
        is_active: cfgActive,
        default_limit: Number(cfgDefaultLimit),
        max_retries: Number(cfgMaxRetries),
        rate_limit_per_min: Number(cfgRateLimit),
      }),
    onSuccess: () => {
      toast.success("DataMoon config saved.");
      setConfigOpen(false);
      void qc.invalidateQueries({ queryKey: ["datamoon-admin-config"] });
    },
    onError: (e: unknown) => {
      toast.error(`Save failed: ${(e as Error).message}`);
    },
  });

  // ── Grant access form ──────────────────────────────────────────────────────
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantTenantId, setGrantTenantId] = useState("");
  const [grantQuota, setGrantQuota] = useState("500");

  const allTenantsQuery = useQuery({
    queryKey: ["platform-tenants"],
    queryFn: () => platformApi.tenants(),
    enabled: grantOpen,
  });

  // Tenants not yet granted access
  const grantedIds = new Set((tenantsQuery.data ?? []).map((r) => r.tenant_id));
  const availableTenants = (allTenantsQuery.data ?? []).filter(
    (t) => !grantedIds.has(Number(t.id)),
  );

  const grantMut = useMutation({
    mutationFn: () =>
      datamoonAdminApi.grantAccess(Number(grantTenantId), {
        prospect_quota: Number(grantQuota),
      }),
    onSuccess: () => {
      toast.success("Access granted. Use the toggle to enable it for the tenant.");
      setGrantOpen(false);
      setGrantTenantId("");
      setGrantQuota("500");
      void qc.invalidateQueries({ queryKey: ["datamoon-admin-tenants"] });
    },
    onError: (e: unknown) => {
      toast.error(`Grant failed: ${(e as Error).message}`);
    },
  });

  // ── Toggle enable ──────────────────────────────────────────────────────────
  const toggleMut = useMutation({
    mutationFn: ({ tenantId, enabled }: { tenantId: number; enabled: boolean }) =>
      datamoonAdminApi.updateAccess(tenantId, { is_enabled: enabled }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["datamoon-admin-tenants"] });
    },
    onError: (e: unknown) => {
      toast.error(`Update failed: ${(e as Error).message}`);
    },
  });

  // ── Quota edit ─────────────────────────────────────────────────────────────
  const [quotaOpen, setQuotaOpen] = useState(false);
  const [quotaTenant, setQuotaTenant] = useState<TenantAccessRow | null>(null);
  const [quotaValue, setQuotaValue] = useState("500");

  function openQuotaEdit(row: TenantAccessRow) {
    setQuotaTenant(row);
    setQuotaValue(String(row.prospect_quota));
    setQuotaOpen(true);
  }

  const quotaMut = useMutation({
    mutationFn: () =>
      datamoonAdminApi.updateAccess(quotaTenant!.tenant_id, {
        prospect_quota: Number(quotaValue),
      }),
    onSuccess: () => {
      toast.success("Quota updated.");
      setQuotaOpen(false);
      void qc.invalidateQueries({ queryKey: ["datamoon-admin-tenants"] });
    },
    onError: (e: unknown) => {
      toast.error(`Update failed: ${(e as Error).message}`);
    },
  });

  // ── Revoke ─────────────────────────────────────────────────────────────────
  const [revokeTarget, setRevokeTarget] = useState<TenantAccessRow | null>(null);

  const revokeMut = useMutation({
    mutationFn: (tenantId: number) => datamoonAdminApi.revokeAccess(tenantId),
    onSuccess: () => {
      toast.success("Access revoked.");
      setRevokeTarget(null);
      void qc.invalidateQueries({ queryKey: ["datamoon-admin-tenants"] });
    },
    onError: (e: unknown) => {
      toast.error(`Revoke failed: ${(e as Error).message}`);
    },
  });

  // ── Render ─────────────────────────────────────────────────────────────────

  const cfg = configQuery.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title="DataMoon Integration"
        description="Manage the global DataMoon API connection and per-tenant access grants."
        actions={
          <Button onClick={openConfigEdit}>
            <Database className="mr-2 h-4 w-4" />
            {cfg ? "Edit Config" : "Set Up DataMoon"}
          </Button>
        }
      />

      {/* Warning banner */}
      <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          This is a platform-level API integration. The API key is shared across all enabled
          tenants. Only SUPER_ADMIN can modify these settings.
        </span>
      </div>

      {/* ── Global config card ── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-4 w-4" />
            Global Configuration
          </CardTitle>
          <CardDescription>DataMoon API credentials and platform defaults.</CardDescription>
        </CardHeader>
        <CardContent>
          {configQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-64" />
              <Skeleton className="h-4 w-48" />
            </div>
          ) : cfg == null ? (
            <EmptyState
              icon={<Database className="h-8 w-8" />}
              title="No config yet"
              description="Click 'Set Up DataMoon' to add the API key and activate the integration."
            />
          ) : (
            <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm sm:grid-cols-3">
              <div>
                <dt className="text-muted-foreground">API Key</dt>
                <dd className="font-mono font-medium">{cfg.api_key_masked}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Status</dt>
                <dd>
                  {cfg.is_active ? (
                    <Badge variant="default" className="gap-1">
                      <CheckCircle2 className="h-3 w-3" /> Active
                    </Badge>
                  ) : (
                    <Badge variant="secondary">Inactive</Badge>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Website ID</dt>
                <dd>{cfg.website_id ?? "—"}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Default Limit</dt>
                <dd>{cfg.default_limit.toLocaleString()} records</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Max Retries</dt>
                <dd>{cfg.max_retries}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Rate Limit</dt>
                <dd>{cfg.rate_limit_per_min} req/min</dd>
              </div>
              <div className="col-span-2 sm:col-span-3">
                <dt className="text-muted-foreground">Last Updated</dt>
                <dd className="text-muted-foreground">{formatDateTime(cfg.updated_at)}</dd>
              </div>
            </dl>
          )}
        </CardContent>
      </Card>

      {/* ── Tenant access card ── */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Tenant Access
            </CardTitle>
            <CardDescription>
              Control which tenants can source prospects from DataMoon and set their monthly
              quotas.
            </CardDescription>
          </div>
          <Button size="sm" onClick={() => setGrantOpen(true)}>
            <Plus className="mr-1 h-4 w-4" />
            Grant Access
          </Button>
        </CardHeader>
        <CardContent>
          {tenantsQuery.isLoading ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : !tenantsQuery.data?.length ? (
            <EmptyState
              icon={<Users className="h-8 w-8" />}
              title="No tenants granted access"
              description="Use 'Grant Access' to allow a tenant to pull prospects from DataMoon."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Enabled</TableHead>
                  <TableHead className="text-right">Quota</TableHead>
                  <TableHead className="text-right">Used</TableHead>
                  <TableHead>Quota Resets</TableHead>
                  <TableHead>Last Pull</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenantsQuery.data.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <p className="font-medium">{row.tenant_name ?? `Tenant ${row.tenant_id}`}</p>
                      <p className="text-xs text-muted-foreground">{row.tenant_slug}</p>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={row.is_enabled}
                        onCheckedChange={(v) =>
                          toggleMut.mutate({ tenantId: row.tenant_id, enabled: v })
                        }
                        disabled={toggleMut.isPending}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <button
                        className="text-sm font-medium underline-offset-2 hover:underline"
                        onClick={() => openQuotaEdit(row)}
                      >
                        {row.prospect_quota.toLocaleString()}
                      </button>
                    </TableCell>
                    <TableCell className="text-right text-sm">
                      {row.quota_used.toLocaleString()}
                      <span className="ml-1 text-xs text-muted-foreground">
                        ({Math.round((row.quota_used / row.prospect_quota) * 100)}%)
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDateTime(row.quota_reset_at)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {row.last_pull_at ? formatDateTime(row.last_pull_at) : "Never"}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-destructive"
                        onClick={() => setRevokeTarget(row)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ── Config dialog ── */}
      {configOpen && (
        <Dialog open onOpenChange={setConfigOpen}>
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-lg rounded-lg bg-background p-6 shadow-xl">
              <DialogHeader>
                <DialogTitle>{cfg ? "Edit DataMoon Config" : "Set Up DataMoon"}</DialogTitle>
                <DialogDescription>
                  {cfg
                    ? "Leave the API key blank to keep the existing key."
                    : "Enter your DataMoon API key to activate the integration."}
                </DialogDescription>
              </DialogHeader>
              <form
                onSubmit={(e: FormEvent) => {
                  e.preventDefault();
                  configMut.mutate();
                }}
                className="mt-4 space-y-4"
              >
                <div className="space-y-1.5">
                  <Label htmlFor="dm-api-key">
                    API Key {cfg && <span className="text-muted-foreground">(leave blank to keep current)</span>}
                  </Label>
                  <Input
                    id="dm-api-key"
                    type="password"
                    placeholder={cfg ? cfg.api_key_masked : "dm_••••••••"}
                    value={cfgApiKey}
                    onChange={(e) => setCfgApiKey(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="dm-website-id">Website ID (optional)</Label>
                  <Input
                    id="dm-website-id"
                    type="number"
                    placeholder="Leave blank for no filter"
                    value={cfgWebsiteId}
                    onChange={(e) => setCfgWebsiteId(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="dm-default-limit">Default Limit (records)</Label>
                    <Input
                      id="dm-default-limit"
                      type="number"
                      min={1}
                      max={5000}
                      value={cfgDefaultLimit}
                      onChange={(e) => setCfgDefaultLimit(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="dm-max-retries">Max Retries</Label>
                    <Input
                      id="dm-max-retries"
                      type="number"
                      min={1}
                      max={10}
                      value={cfgMaxRetries}
                      onChange={(e) => setCfgMaxRetries(e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="dm-rate-limit">Rate Limit (req/min)</Label>
                  <Input
                    id="dm-rate-limit"
                    type="number"
                    min={1}
                    max={600}
                    value={cfgRateLimit}
                    onChange={(e) => setCfgRateLimit(e.target.value)}
                  />
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    id="dm-active"
                    checked={cfgActive}
                    onCheckedChange={setCfgActive}
                  />
                  <Label htmlFor="dm-active">Active (enable the integration globally)</Label>
                </div>
                <DialogFooter className="pt-2">
                  <DialogClose asChild>
                    <Button variant="outline" type="button">Cancel</Button>
                  </DialogClose>
                  <Button type="submit" disabled={configMut.isPending}>
                    {configMut.isPending ? "Saving…" : "Save Config"}
                  </Button>
                </DialogFooter>
              </form>
            </div>
          </div>
        </Dialog>
      )}

      {/* ── Grant access dialog ── */}
      {grantOpen && (
        <Dialog open onOpenChange={setGrantOpen}>
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-md rounded-lg bg-background p-6 shadow-xl">
              <DialogHeader>
                <DialogTitle>Grant DataMoon Access</DialogTitle>
                <DialogDescription>
                  Select a tenant and set their monthly prospect quota. Access is created
                  disabled — enable it using the toggle after granting.
                </DialogDescription>
              </DialogHeader>
              <form
                onSubmit={(e: FormEvent) => {
                  e.preventDefault();
                  grantMut.mutate();
                }}
                className="mt-4 space-y-4"
              >
                <div className="space-y-1.5">
                  <Label htmlFor="grant-tenant">Tenant</Label>
                  {allTenantsQuery.isLoading ? (
                    <Skeleton className="h-9 w-full" />
                  ) : availableTenants.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      All tenants already have access granted.
                    </p>
                  ) : (
                    <NativeSelect
                      id="grant-tenant"
                      value={grantTenantId}
                      onChange={(e) => setGrantTenantId(e.target.value)}
                      required
                    >
                      <option value="">— select a tenant —</option>
                      {availableTenants.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name ?? t.slug}
                        </option>
                      ))}
                    </NativeSelect>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="grant-quota">Monthly Prospect Quota</Label>
                  <Input
                    id="grant-quota"
                    type="number"
                    min={1}
                    value={grantQuota}
                    onChange={(e) => setGrantQuota(e.target.value)}
                    required
                  />
                </div>
                <DialogFooter className="pt-2">
                  <DialogClose asChild>
                    <Button variant="outline" type="button">Cancel</Button>
                  </DialogClose>
                  <Button
                    type="submit"
                    disabled={grantMut.isPending || !grantTenantId || availableTenants.length === 0}
                  >
                    {grantMut.isPending ? "Granting…" : "Grant Access"}
                  </Button>
                </DialogFooter>
              </form>
            </div>
          </div>
        </Dialog>
      )}

      {/* ── Quota edit dialog ── */}
      {quotaOpen && quotaTenant && (
        <Dialog open onOpenChange={setQuotaOpen}>
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-sm rounded-lg bg-background p-6 shadow-xl">
              <DialogHeader>
                <DialogTitle>Edit Quota</DialogTitle>
                <DialogDescription>
                  Change the monthly prospect limit for{" "}
                  <strong>{quotaTenant.tenant_name ?? quotaTenant.tenant_slug}</strong>.
                </DialogDescription>
              </DialogHeader>
              <form
                onSubmit={(e: FormEvent) => {
                  e.preventDefault();
                  quotaMut.mutate();
                }}
                className="mt-4 space-y-4"
              >
                <div className="space-y-1.5">
                  <Label htmlFor="quota-val">Monthly Quota</Label>
                  <Input
                    id="quota-val"
                    type="number"
                    min={1}
                    value={quotaValue}
                    onChange={(e) => setQuotaValue(e.target.value)}
                    required
                  />
                </div>
                <DialogFooter className="pt-2">
                  <DialogClose asChild>
                    <Button variant="outline" type="button">Cancel</Button>
                  </DialogClose>
                  <Button type="submit" disabled={quotaMut.isPending}>
                    {quotaMut.isPending ? "Saving…" : "Save"}
                  </Button>
                </DialogFooter>
              </form>
            </div>
          </div>
        </Dialog>
      )}

      {/* ── Revoke confirm dialog ── */}
      {revokeTarget && (
        <Dialog open onOpenChange={() => setRevokeTarget(null)}>
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-sm rounded-lg bg-background p-6 shadow-xl">
              <DialogHeader>
                <DialogTitle>Revoke DataMoon Access</DialogTitle>
                <DialogDescription>
                  Remove DataMoon access for{" "}
                  <strong>{revokeTarget.tenant_name ?? revokeTarget.tenant_slug}</strong>? This
                  will immediately prevent the tenant from pulling prospects.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={() => setRevokeTarget(null)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  disabled={revokeMut.isPending}
                  onClick={() => revokeMut.mutate(revokeTarget.tenant_id)}
                >
                  {revokeMut.isPending ? "Revoking…" : "Revoke Access"}
                </Button>
              </DialogFooter>
            </div>
          </div>
        </Dialog>
      )}
    </div>
  );
}
